import { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ScanProgressProps {
  isScanning: boolean;
  onComplete?: () => void;
}

// Time-based stages (in seconds)
const stages = [
  { label: "Connecting to AI platforms...", startTime: 0 },
  { label: "Running Discovery Mode query...", startTime: 5 },
  { label: "Analyzing AI recommendations...", startTime: 12 },
  { label: "Verifying brand presence...", startTime: 22 },
  { label: "Calculating visibility score...", startTime: 32 },
];

const ScanProgress = ({ isScanning, onComplete }: ScanProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Get current stage based on elapsed time
  const getCurrentStageIndex = (seconds: number): number => {
    for (let i = stages.length - 1; i >= 0; i--) {
      if (seconds >= stages[i].startTime) {
        return i;
      }
    }
    return 0;
  };

  const currentStageIndex = getCurrentStageIndex(elapsedTime);

  // Humanized progress animation with random increments and occasional pauses
  useEffect(() => {
    if (!isScanning) {
      setProgress(0);
      setElapsedTime(0);
      setStartTime(null);
      return;
    }

    // Set start time when scanning begins
    if (startTime === null) {
      setStartTime(Date.now());
    }

    let currentProgress = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const updateProgress = () => {
      // Update elapsed time for stage display
      const now = Date.now();
      const elapsed = now - (startTime || now);
      setElapsedTime(Math.floor(elapsed / 1000));

      if (currentProgress >= 98) {
        // Cap at 98%, wait for actual completion
        return;
      }

      // Random increment based on current progress phase
      let increment: number;
      let delay: number;

      if (currentProgress < 30) {
        // Fast start: big jumps, short delays
        increment = Math.random() * 8 + 3;  // 3-11%
        delay = Math.random() * 400 + 200;   // 200-600ms
      } else if (currentProgress < 70) {
        // Medium phase: moderate jumps
        increment = Math.random() * 5 + 2;  // 2-7%
        delay = Math.random() * 800 + 400;   // 400-1200ms
      } else {
        // Slow finish: tiny increments, longer waits
        increment = Math.random() * 2 + 0.5; // 0.5-2.5%
        delay = Math.random() * 1500 + 800;  // 800-2300ms
      }

      // 10% chance of "thinking pause" (long stall) in the middle phase
      if (Math.random() < 0.1 && currentProgress > 20 && currentProgress < 80) {
        delay = Math.random() * 2000 + 2000; // 2-4 second pause
      }

      currentProgress = Math.min(98, currentProgress + increment);
      setProgress(currentProgress);

      timeoutId = setTimeout(updateProgress, delay);
    };

    // Start the animation
    updateProgress();

    return () => clearTimeout(timeoutId);
  }, [isScanning, startTime]);

  // Handle completion (called externally when API finishes)
  useEffect(() => {
    if (!isScanning && progress > 0) {
      // Jump to 100% when scan completes
      setProgress(100);
      onComplete?.();
    }
  }, [isScanning, progress, onComplete]);

  if (!isScanning) return null;

  return (
    <div className="w-full space-y-5 animate-fade-in">
      {/* Progress bar */}
      <div className="relative h-2.5 md:h-3 bg-[#E5E7EB] dark:bg-gray-700 rounded-full overflow-hidden">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
      </div>
      
      {/* Stages with checkmarks */}
      <div className="space-y-2">
        {stages.map((stage, index) => {
          const isComplete = index < currentStageIndex;
          const isCurrent = index === currentStageIndex;
          
          return (
            <div 
              key={index}
              className={cn(
                "flex items-center gap-3 text-sm transition-all duration-300",
                isComplete && "text-primary",
                isCurrent && "text-foreground font-medium",
                !isComplete && !isCurrent && "text-muted-foreground/50"
              )}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                {isComplete ? (
                  <Check className="w-4 h-4 text-primary animate-bounce-in" />
                ) : isCurrent ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                )}
              </div>
              <span className={cn(isCurrent && "animate-pulse-subtle")}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Time estimate - always visible */}
      <p className="text-sm text-muted-foreground text-center">
        AI visibility scan takes ~30-45 seconds
      </p>
    </div>
  );
};

export default ScanProgress;
